import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import ProposalList from './pages/ProposalList';
import ProposalEditor from './pages/ProposalEditor';
import Snippets from './pages/Snippets';
import Settings from './pages/Settings';
import { db, initSettings, seedDefaultSnippets } from './db/database';
import { restoreLatestBackup, resolveBackupUrl } from './services/cloudBackup';
import * as authService from './services/authService';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import './index.css';

const STARTUP_DEADLINE_MS = 20000;

const PremiumBackground = ({ children, zIndex = "z-[300]" }) => (
  <div className={`fixed inset-0 ${zIndex} bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-y-auto overflow-x-hidden min-h-[100dvh] flex flex-col items-center p-4 sm:p-6`}>
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
      <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] bg-indigo-600/20 blur-[120px] rounded-full mix-blend-screen"></div>
      <div className="absolute top-[60%] -right-[10%] w-[60%] h-[60%] bg-emerald-600/10 blur-[120px] rounded-full mix-blend-screen"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] [mask-image:linear-gradient(to_bottom,white,transparent)]"></div>
    </div>
    <div className="relative z-10 w-full flex flex-col items-center mt-2 sm:my-auto mb-auto">{children}</div>
  </div>
);

function LoginModal({ codeDigits, onDigitChange, onDigitKeyDown, onPaste, loginError, loginLoading, showCode, onToggleShowCode, onLogin, onDigitFocus }) {
  const btnDisabled = loginLoading || codeDigits.join('').length !== 10;
  return (
    <PremiumBackground zIndex="z-[300]">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}
        className="w-full max-w-[760px] bg-white rounded-[24px] sm:rounded-[28px] shadow-2xl shadow-slate-900/50 overflow-hidden flex flex-col lg:flex-row shrink-0 border border-white/20">
        <div className="hidden lg:flex flex-col justify-between w-[320px] bg-gradient-to-br from-indigo-900 via-indigo-800 to-slate-900 p-10 text-white shrink-0 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] [mask-image:linear-gradient(to_bottom,white,transparent)]"></div>
          <div className="absolute -top-24 -left-24 w-64 h-64 bg-indigo-500/30 blur-[60px] rounded-full"></div>
          <div className="relative z-10">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center text-3xl mb-6 border border-white/20 shadow-xl">🛡️</div>
            <h2 className="text-2xl font-black mb-2 tracking-tight">ScopeShield</h2>
            <p className="text-indigo-200 text-sm font-medium leading-relaxed">Enterprise-grade protection for your professional Statements of Work.</p>
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10 backdrop-blur-sm">
              <SafeIcon icon={FiIcons.FiShield} className="text-emerald-400 text-xl shrink-0" />
              <p className="text-xs text-indigo-100 font-medium">Secure, authenticated access required to view or modify workspace data.</p>
            </div>
          </div>
        </div>
        <div className="flex-1 p-5 sm:p-10 flex flex-col relative bg-white">
          <div className="lg:hidden flex items-center gap-4 mb-6 sm:mb-8 pb-5 sm:pb-6 border-b border-gray-100">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-2xl shrink-0 shadow-sm">🛡️</div>
            <div>
              <h2 className="text-lg font-black text-gray-900 tracking-tight leading-none mb-1">ScopeShield</h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Secure Workspace</p>
            </div>
          </div>
          <div className="mb-6 sm:mb-8">
            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] leading-none mb-2">ScopeShield Secure Access</p>
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mb-3">Enter your access code</h2>
            <p className="text-sm text-gray-500 font-medium leading-relaxed">Please enter the 10-digit access code assigned by your organisation administrator to continue.</p>
          </div>
          <div className="mb-6 sm:mb-8">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">10-Digit Access Code</label>
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full">
              <div className="grid grid-cols-5 gap-1.5 sm:gap-2 w-full min-w-0">
                {[0, 1, 2, 3, 4].map(i => (
                  <input key={i} type={showCode ? 'text' : 'password'} inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"} value={codeDigits[i]}
                    onChange={e => onDigitChange(i, e.target.value)} onKeyDown={e => onDigitKeyDown(i, e)} onPaste={onPaste} onFocus={onDigitFocus} maxLength={1} disabled={loginLoading}
                    aria-label={`Access code digit ${i + 1}`}
                    className={`w-full min-w-0 h-12 sm:h-16 text-center font-mono text-xl sm:text-2xl font-black border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${loginError ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-900'} ${codeDigits[i] && !loginError ? 'border-indigo-200 bg-indigo-50/30' : ''}`} />
                ))}
              </div>
              <span className="hidden sm:block text-gray-300 font-black mx-1 select-none">—</span>
              <div className="grid grid-cols-5 gap-1.5 sm:gap-2 w-full min-w-0">
                {[5, 6, 7, 8, 9].map(i => (
                  <input key={i} type={showCode ? 'text' : 'password'} inputMode="numeric" autoComplete="off" value={codeDigits[i]}
                    onChange={e => onDigitChange(i, e.target.value)} onKeyDown={e => onDigitKeyDown(i, e)} onPaste={onPaste} onFocus={onDigitFocus} maxLength={1} disabled={loginLoading}
                    aria-label={`Access code digit ${i + 1}`}
                    className={`w-full min-w-0 h-12 sm:h-16 text-center font-mono text-xl sm:text-2xl font-black border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${loginError ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-900'} ${codeDigits[i] && !loginError ? 'border-indigo-200 bg-indigo-50/30' : ''}`} />
                ))}
              </div>
            </div>
            <div className="flex justify-end mt-3 sm:mt-4">
              <button onClick={onToggleShowCode} className="flex items-center gap-1.5 text-[10px] font-black text-gray-400 hover:text-indigo-600 uppercase tracking-widest transition-colors p-2 -mr-2 rounded-lg hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500/30 focus:outline-none">
                <SafeIcon icon={showCode ? FiIcons.FiEyeOff : FiIcons.FiEye} className="text-sm" />
                {showCode ? 'Hide Code' : 'Show Code'}
              </button>
            </div>
          </div>
          {loginError && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 font-bold break-words leading-relaxed">{loginError}</p>
              </div>
            </div>
          )}
          <div className="mt-auto pt-2">
            <button onClick={onLogin} disabled={btnDisabled}
              className={`w-full py-3.5 sm:py-4 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 focus:ring-4 focus:ring-indigo-500/30 focus:outline-none ${btnDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none border border-gray-200' : 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white hover:from-indigo-700 hover:to-indigo-600 shadow-indigo-600/20 border border-indigo-600'}`}>
              {loginLoading && <SafeIcon icon={FiIcons.FiRefreshCw} className="animate-spin" />}
              {loginLoading ? 'Verifying…' : 'Sign In'}
            </button>
          </div>
          <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-gray-100 text-center">
            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">ScopeShield • Secure Workspace Access</p>
          </div>
        </div>
      </motion.div>
    </PremiumBackground>
  );
}

function HydrationOverlay({ status, onRetry, onContinueLocal }) {
  if (status === 'ready') return null;
  return (
    <PremiumBackground zIndex="z-[200]">
      {status === 'checking' && (
        <div className="relative w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[28px] p-8 sm:p-12 text-center shadow-2xl shrink-0">
          <div className="w-20 h-20 bg-indigo-500/20 border border-indigo-500/30 rounded-2xl flex items-center justify-center text-4xl mb-8 mx-auto animate-pulse shadow-inner">🛡️</div>
          <h2 className="text-white text-2xl font-black mb-3 tracking-tight">Checking cloud workspace</h2>
          <p className="text-indigo-200 text-sm font-medium leading-relaxed mb-8">Looking for your existing proposals and settings.</p>
          <div className="w-full h-1.5 bg-slate-800/50 rounded-full overflow-hidden mb-8">
            <div className="h-full bg-indigo-400 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
          <button onClick={onContinueLocal} className="w-full py-4 bg-slate-800/80 text-white border border-white/10 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all focus:ring-4 focus:ring-white/10 focus:outline-none">Continue with Local Workspace</button>
        </div>
      )}
      {status === 'restoring' && (
        <div className="relative w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[28px] p-8 sm:p-12 text-center shadow-2xl shrink-0">
          <div className="w-20 h-20 bg-emerald-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center text-4xl mb-8 mx-auto animate-pulse shadow-inner">🛡️</div>
          <h2 className="text-white text-2xl font-black mb-3 tracking-tight">Restoring workspace</h2>
          <p className="text-emerald-200 text-sm font-medium leading-relaxed mb-8">Downloading your secure proposals and settings.</p>
          <div className="w-full h-1.5 bg-slate-800/50 rounded-full overflow-hidden mb-8">
            <div className="h-full bg-emerald-400 rounded-full animate-pulse" style={{ width: '80%' }}></div>
          </div>
          <button onClick={onContinueLocal} className="w-full py-4 bg-slate-800/80 text-white border border-white/10 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all focus:ring-4 focus:ring-white/10 focus:outline-none">Continue with Local Workspace</button>
        </div>
      )}
      {status === 'cloud-error' && (
        <div className="relative w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[28px] p-8 sm:p-12 text-center shadow-2xl shrink-0">
          <div className="w-20 h-20 bg-amber-500/20 border border-amber-500/30 rounded-2xl flex items-center justify-center text-4xl mb-8 mx-auto shadow-inner">🛡️</div>
          <h2 className="text-white text-2xl font-black mb-3 tracking-tight">Cloud Restore Unavailable</h2>
          <p className="text-amber-200 text-sm font-medium leading-relaxed mb-8">Could not reach your cloud workspace. You can retry or start locally.</p>
          <div className="flex flex-col gap-3">
            <button onClick={onRetry} className="w-full py-4 bg-white text-slate-900 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest hover:bg-gray-100 transition-all shadow-xl shadow-white/10 focus:ring-4 focus:ring-white/30 focus:outline-none">Retry Cloud Restore</button>
            <button onClick={onContinueLocal} className="w-full py-4 bg-slate-800/80 text-white border border-white/10 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all focus:ring-4 focus:ring-white/10 focus:outline-none">Continue Locally</button>
          </div>
        </div>
      )}
    </PremiumBackground>
  );
}

function App() {
  const [authPhase, setAuthPhase] = useState('checking');
  const [authErrorMsg, setAuthErrorMsg] = useState(null);
  const [codeDigits, setCodeDigits] = useState(Array(10).fill(''));
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [hydrationStatus, setHydrationStatus] = useState('checking');
  const abortControllerRef = useRef(null);
  const runTokenRef = useRef(0);
  const deadlineTimerRef = useRef(null);
  const hydrationStartedRef = useRef(false);

  useEffect(() => {
    performAuthCheck();
  }, []);

  const performAuthCheck = async () => {
    setAuthPhase('checking');
    setAuthErrorMsg(null);
    try {
      const settings = await initSettings();
      const status = await authService.authStatus(settings);
      if (!status.authEnabled) {
        setAuthPhase('disabled');
        return;
      }
      try {
        const validation = await authService.validateSession(settings);
        if (validation.valid) {
          setAuthPhase('authenticated');
        } else {
          setAuthPhase('required');
        }
      } catch (validationErr) {
        // Transient error — preserve session, show retryable error
        console.error('Session validation failed (transient):', validationErr);
        setAuthErrorMsg(
          'ScopeShield could not verify the session because the approval service did not respond. Your session has not been deleted. Retry the connection.'
        );
        setAuthPhase('error');
      }
    } catch (err) {
      console.error('Auth verification failed:', err);
      setAuthErrorMsg(err.message || 'Unable to verify authentication status.');
      setAuthPhase('error');
    }
  };

  useEffect(() => {
    return authService.onAuthRequired(() => {
      setAuthPhase('required');
      setCodeDigits(Array(10).fill(''));
      setLoginError(null);
      setLoginLoading(false);
    });
  }, []);

  const performLogin = async (code) => {
    setLoginLoading(true);
    setLoginError(null);
    try {
      const settings = await initSettings();
      await authService.login(settings, code);
      setAuthPhase('authenticated');
    } catch (err) {
      setLoginError(err.message || 'Login failed. Please check your access code.');
      setLoginLoading(false);
    }
  };

  const handleLogin = () => {
    const code = codeDigits.join('');
    if (code.length !== 10 || !/^\d{10}$/.test(code)) {
      setLoginError('Please enter all 10 digits of your access code.');
      return;
    }
    performLogin(code);
  };

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...codeDigits];
    newDigits[index] = digit;
    setCodeDigits(newDigits);
    setLoginError(null);
    if (digit && index < 9) {
      setTimeout(() => {
        const nextInput = document.querySelectorAll('[inputmode="numeric"]')[index + 1];
        if (nextInput) nextInput.focus();
      }, 0);
    }
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!codeDigits[index] && index > 0) {
        const newDigits = [...codeDigits];
        newDigits[index - 1] = '';
        setCodeDigits(newDigits);
        setLoginError(null);
        setTimeout(() => {
          const prevInput = document.querySelectorAll('[inputmode="numeric"]')[index - 1];
          if (prevInput) prevInput.focus();
        }, 0);
      } else if (codeDigits[index]) {
        const newDigits = [...codeDigits];
        newDigits[index] = '';
        setCodeDigits(newDigits);
        setLoginError(null);
      }
    }
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 10);
    if (pasted.length > 0) {
      const newDigits = Array(10).fill('');
      for (let i = 0; i < pasted.length; i++) {
        newDigits[i] = pasted[i];
      }
      setCodeDigits(newDigits);
      setLoginError(null);
      const focusIdx = Math.min(pasted.length, 9);
      setTimeout(() => {
        const inputs = document.querySelectorAll('[inputmode="numeric"]');
        if (inputs[focusIdx]) inputs[focusIdx].focus();
      }, 0);
    }
  };

  const handleDigitFocus = (e) => e.target.select();

  const startHydration = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;
    const currentToken = ++runTokenRef.current;
    const isCurrentRun = () => runTokenRef.current === currentToken;

    if (isCurrentRun()) setHydrationStatus('checking');
    const myDeadlineTimer = setTimeout(() => {
      if (isCurrentRun()) {
        runTokenRef.current += 1;
        controller.abort();
        setHydrationStatus('cloud-error');
      }
    }, STARTUP_DEADLINE_MS);
    deadlineTimerRef.current = myDeadlineTimer;

    try {
      const settings = await initSettings();
      const count = await db.proposals.count();
      if (!isCurrentRun() || signal.aborted) return;
      if (count > 0 || settings.workspaceInitialized) {
        if (isCurrentRun()) setHydrationStatus('ready');
        return;
      }
      const url = resolveBackupUrl(settings);
      if (!url) {
        if (!isCurrentRun() || signal.aborted) return;
        await seedDefaultSnippets();
        if (!isCurrentRun() || signal.aborted) return;
        await db.settings.update('global', { workspaceInitialized: true });
        if (!isCurrentRun() || signal.aborted) return;
        if (isCurrentRun()) setHydrationStatus('ready');
        return;
      }
      if (isCurrentRun()) setHydrationStatus('restoring');
      try {
        const result = await restoreLatestBackup(settings, { signal });
        if (!isCurrentRun()) return;
        if (result.restored) {
          if (!isCurrentRun() || signal.aborted) return;
          await db.settings.update('global', { workspaceInitialized: true, googleBackupConnected: true });
          if (!isCurrentRun() || signal.aborted) return;
          if (isCurrentRun()) setHydrationStatus('ready');
          return;
        }
        if (result.reason === 'no_backup') {
          if (!isCurrentRun() || signal.aborted) return;
          await seedDefaultSnippets();
          if (!isCurrentRun() || signal.aborted) return;
          await db.settings.update('global', { workspaceInitialized: true });
          if (!isCurrentRun() || signal.aborted) return;
          if (isCurrentRun()) setHydrationStatus('ready');
          return;
        }
        if (isCurrentRun()) setHydrationStatus('cloud-error');
      } catch (cloudErr) {
        if (!isCurrentRun()) return;
        const msg = cloudErr.message || '';
        if (msg === 'Request was cancelled.' || msg.startsWith('Restore cancelled')) {
          return;
        }
        console.warn('ScopeShield: Cloud hydration failed.', cloudErr);
        if (isCurrentRun()) setHydrationStatus('cloud-error');
      }
    } catch (err) {
      if (!isCurrentRun()) return;
      console.error('ScopeShield: Hydration error.', err);
      setHydrationStatus('cloud-error');
    } finally {
      clearTimeout(myDeadlineTimer);
      if (deadlineTimerRef.current === myDeadlineTimer) {
        deadlineTimerRef.current = null;
      }
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (authPhase !== 'authenticated' && authPhase !== 'disabled') return;
    if (hydrationStartedRef.current) return;
    hydrationStartedRef.current = true;
    startHydration();
    return () => {
      runTokenRef.current += 1;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (deadlineTimerRef.current) {
        clearTimeout(deadlineTimerRef.current);
        deadlineTimerRef.current = null;
      }
    };
  }, [authPhase, startHydration]);

  const handleRetry = () => {
    startHydration();
  };

  const handleContinueLocal = async () => {
    runTokenRef.current++;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = null;
    if (deadlineTimerRef.current) {
      clearTimeout(deadlineTimerRef.current);
      deadlineTimerRef.current = null;
    }
    try {
      await seedDefaultSnippets();
      await db.settings.update('global', { workspaceInitialized: true });
    } catch (err) {
      console.error('ScopeShield: Local setup error.', err);
    }
    setHydrationStatus('ready');
  };

  if (authPhase === 'checking') {
    return (
      <PremiumBackground zIndex="z-[300]">
        <div className="relative w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[28px] p-8 sm:p-12 text-center shadow-2xl shrink-0">
          <div className="w-20 h-20 bg-indigo-500/20 border border-indigo-500/30 rounded-2xl flex items-center justify-center text-4xl mb-8 mx-auto animate-pulse shadow-inner">🛡️</div>
          <h2 className="text-white text-2xl font-black mb-3 tracking-tight">Checking workspace access</h2>
          <p className="text-indigo-200 text-sm font-medium leading-relaxed mb-8">Verifying your ScopeShield credentials securely.</p>
          <div className="w-full h-1.5 bg-slate-800/50 rounded-full overflow-hidden mb-2">
            <div className="h-full bg-indigo-400 rounded-full animate-pulse" style={{ width: '40%' }}></div>
          </div>
        </div>
      </PremiumBackground>
    );
  }

  if (authPhase === 'error') {
    return (
      <PremiumBackground zIndex="z-[300]">
        <div className="relative w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[28px] p-8 sm:p-12 text-center shadow-2xl shrink-0">
          <div className="w-20 h-20 bg-amber-500/20 border border-amber-500/30 rounded-2xl flex items-center justify-center text-4xl mb-8 mx-auto shadow-inner">🛡️</div>
          <h2 className="text-white text-2xl font-black mb-3 tracking-tight">Connection Unavailable</h2>
          <p className="text-amber-200 text-sm font-medium leading-relaxed mb-6">{authErrorMsg || 'ScopeShield could not reach the authentication service.'}</p>
          <div className="bg-slate-900/50 rounded-xl p-4 mb-8 text-left border border-white/10 shadow-inner overflow-hidden">
            <p className="text-amber-300 text-xs font-mono break-words">Your session has not been deleted. Use Retry to verify the connection again.</p>
          </div>
          <button onClick={performAuthCheck} className="w-full py-4 bg-white text-slate-900 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest hover:bg-gray-100 transition-all shadow-xl shadow-white/10 focus:ring-4 focus:ring-white/30 focus:outline-none">
            Retry Connection
          </button>
        </div>
      </PremiumBackground>
    );
  }

  if (authPhase === 'required') {
    return (
      <LoginModal
        codeDigits={codeDigits}
        onDigitChange={handleDigitChange}
        onDigitKeyDown={handleDigitKeyDown}
        onPaste={handlePaste}
        onDigitFocus={handleDigitFocus}
        loginError={loginError}
        loginLoading={loginLoading}
        showCode={showCode}
        onToggleShowCode={() => setShowCode(prev => !prev)}
        onLogin={handleLogin}
      />
    );
  }

  return (
    <>
      <HydrationOverlay status={hydrationStatus} onRetry={handleRetry} onContinueLocal={handleContinueLocal} />
      {hydrationStatus === 'ready' && (
        <HashRouter>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route path="proposals" element={<ProposalList />} />
              <Route path="proposals/:id" element={<ProposalEditor />} />
              <Route path="library" element={<Snippets />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Routes>
        </HashRouter>
      )}
    </>
  );
}

export default App;